import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  Collapse,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import {
  FiHome,
  FiUsers,
  FiChevronDown,
  FiChevronRight,
  FiMenu,
  FiGlobe,
  FiClock,
} from "react-icons/fi";
import {
  MdOutlineReceiptLong,
  MdOutlineAssessment,
} from "react-icons/md";
import { FaCoins, FaLandmark } from "react-icons/fa";
import { IoGameControllerOutline } from "react-icons/io5";
import { useLocation, useNavigate, Link } from "react-router-dom";

const SIDEBAR_WIDTH = "240px";

const NavItem = ({ icon, label, to, onClick, sidebarRef, scrollPos }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = location.pathname === to;

  const handleClick = (e) => {
    if (to) {
      e.preventDefault();
      // 🔒 save sidebar scroll
      if (sidebarRef?.current) {
        scrollPos.current = sidebarRef.current.scrollTop;
      }
      navigate(to);
      onClick?.();
    }
  };

  return (
    <Flex
      as={to ? Link : "div"}
      to={to}
      align="center"
      gap={3}
      px={4}
      py={2}
      cursor="pointer"
      borderRadius="md"
      bg={isActive ? "#0f06b9" : "transparent"}
      _hover={{ bg: "#382ad1", color: "white" }}
      color="white"
      onClick={handleClick}
      transition="0.2s"
      mb={1}
    >
      {icon && <Icon as={icon} boxSize={5} />}
      <Text fontSize="sm" flex="1" textAlign="left" my="auto">
        {label}
      </Text>
    </Flex>
  );
};

const SectionHeader = ({ label, icon, sectionKey, isOpen, toggleSection }) => {
  return (
    <Flex
      px={4}
      py={2}
      align="center"
      justify="space-between"
      cursor="pointer"
      borderRadius="md"
      _hover={{ bg: "#1e2a78" }}
      color="white"
      onClick={() => toggleSection(sectionKey)}
      transition="0.2s"
    >
      <Flex align="center">
        <Icon as={icon} mr={3} boxSize={5} />
        <Text fontSize="sm" fontWeight="medium" my="auto">
          {label}
        </Text>
      </Flex>
      <Icon as={isOpen ? FiChevronDown : FiChevronRight} />
    </Flex>
  );
};

const Sidebar = () => {
  const drawer = useDisclosure();
  const location = useLocation();
  const [openSection, setOpenSection] = useState(null);
  const sidebarRef = useRef(null);
  const scrollPos = useRef(0);

  const toggleSection = (key) => {
    setOpenSection((prev) => (prev === key ? null : key));
  };

  useEffect(() => {
    if (sidebarRef.current) {
      sidebarRef.current.scrollTop = scrollPos.current;
    }
  }, [location.pathname]);

  const SidebarContent = ({ onClose }) => (
    <Box
      ref={sidebarRef}
      bg="#191a1e"
      w="100%"
      maxW={SIDEBAR_WIDTH}
      h="100vh"
      overflowY="auto"
      color="white"
      p={4}
      pb={20}
      css={{
        "&::-webkit-scrollbar": {
          width: "4px",
        },
        "&::-webkit-scrollbar-track": {
          width: "6px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "rgba(255, 255, 255, 0.2)",
          borderRadius: "24px",
        },
      }}
    >
      {/* <Text fontSize="2xl" fontWeight="bold" mb={6} px={4}>
        JSK <span style={{ color: "#6366f1" }}>Book</span>
      </Text> */}

      <NavItem
        icon={FiHome}
        label="Dashboard"
        to="/"
        onClick={onClose}
        sidebarRef={sidebarRef}
        scrollPos={scrollPos}
      />

      {/* Users */}
      <SectionHeader
        label="Users"
        icon={FiUsers}
        sectionKey="users"
        isOpen={openSection === "users"}
        toggleSection={toggleSection}
      />
      <Collapse in={openSection === "users"}>
        <Box ml={4} mt={1}>
          <NavItem
            label="All Users"
            to="/users"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
        </Box>
      </Collapse>

      {/* Casino Management */}
      <SectionHeader
        label="Casino"
        icon={IoGameControllerOutline}
        sectionKey="casino"
        isOpen={openSection === "casino"}
        toggleSection={toggleSection}
      />
      <Collapse in={openSection === "casino"}>
        <Box ml={4} mt={1}>
          <NavItem
            label="All Providers"
            to="/casino/providers"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
          <NavItem
            label="All Games"
            to="/casino/games"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
          <NavItem
            label="Analytics"
            to="/casino/analytics"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
        </Box>
      </Collapse>

      {/* Game Reports */}
      <SectionHeader
        label="Game Reports"
        icon={MdOutlineAssessment}
        sectionKey="game-reports"
        isOpen={openSection === "game-reports"}
        toggleSection={toggleSection}
      />
      <Collapse in={openSection === "game-reports"}>
        <Box ml={4} mt={1}>
          <NavItem
            label="Bet History"
            to="/reports/bet"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
          <NavItem
            label="Casino Game Reports"
            to="/reports/casino"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
          <NavItem
            label="Sports Game Reports"
            to="/reports/sports"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
        </Box>
      </Collapse>

      {/* Transaction History */}
      <NavItem
        icon={MdOutlineReceiptLong}
        label="Transaction History"
        to="/transaction-history"
        onClick={onClose}
        sidebarRef={sidebarRef}
        scrollPos={scrollPos}
      />
      <NavItem
        icon={MdOutlineAssessment}
        label="Credit History"
        to="/reports/credit"
        onClick={onClose}
        sidebarRef={sidebarRef}
        scrollPos={scrollPos}
      />

      {/* MANAGE BETTORS */}
      {/* <SectionHeader
        label="Manage Bettors"
        icon={FiUsers}
        sectionKey="bettors"
        isOpen={openSection === "bettors"}
        toggleSection={toggleSection}
      />
      <Collapse in={openSection === "bettors"}>
        <Box ml={4} mt={1}>
          <NavItem
            label="Active Bettors"
            to="/active-bettors"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
          <NavItem
            label="Banned Bettors"
            to="/banned-bettors"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
          <NavItem
            label="Email Unverified"
            to="/email-unverified"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
          <NavItem
            label="Mobile Unverified"
            to="/mobile-unverified"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
          <NavItem
            label="KYC Unverified"
            to="/kyc-unverified"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
          <NavItem
            label="KYC Pending"
            to="/kyc-pending"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
          <NavItem
            label="With Balance"
            to="/with-balance"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
          <NavItem
            label="Send Notification"
            to="/send-notification"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
          <NavItem
            label="All Bettors"
            to="/all-bettors"
            onClick={onClose}
            sidebarRef={sidebarRef}
            scrollPos={scrollPos}
          />
        </Box>
      </Collapse> */}

      {/* <Text
        fontSize="xs"
        mt={6}
        mb={2}
        px={4}
        opacity={0.6}
        fontWeight="bold"
        textAlign="left"
        letterSpacing="wider"
      >
        BETTING
      </Text>
      <NavItem
        label="Categories"
        to="/categories"
        icon={FaFutbol}
        onClick={onClose}
        sidebarRef={sidebarRef}
        scrollPos={scrollPos}
      />
      <NavItem
        label="Teams"
        to="/teams"
        icon={FiUsers}
        onClick={onClose}
        sidebarRef={sidebarRef}
        scrollPos={scrollPos}
      /> */}

      {/* <SectionHeader
        label="Leagues"
        icon={SiSpotify}
        sectionKey="leagues"
        isOpen={openSection === "leagues"}
        toggleSection={toggleSection}
      />
      <Collapse in={openSection === "leagues"}>
        <Box ml={4} mt={1}>
          <NavItem label="In Season" to="/in-season-leagues" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="In Season Enabled" to="/in-season-enable" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="API Enabled" to="/api-enabled" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Manual Enabled" to="/manual-enabled" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="In Season Disabled" to="/in-season-disabled" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="All Leagues" to="/all-leagues" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
        </Box>
      </Collapse> */}

      {/* <SectionHeader
        label="Manage Games"
        icon={IoGameControllerOutline}
        sectionKey="games"
        isOpen={openSection === "games"}
        toggleSection={toggleSection}
      />
      <Collapse in={openSection === "games"}>
        <Box ml={4} mt={1}>
          <NavItem label="Open for Betting" to="/open-betting-games" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Closed for Betting" to="/closed-betting" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Not Open for Betting" to="/not-open-betting" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="In Play Game" to="/in-play-games" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Upcoming Game" to="/upcoming-games" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Ended Game" to="/ended-games" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Cancelled Game" to="/cancelled-games" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="All Game" to="/all-games" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
        </Box>
      </Collapse> */}

      {/* <SectionHeader
        label="Bets Placed"
        icon={FiClipboard}
        sectionKey="bets-placed"
        isOpen={openSection === "bets-placed"}
        toggleSection={toggleSection}
      />
      <Collapse in={openSection === "bets-placed"}>
        <Box ml={4} mt={1}>
          <NavItem label="Pending Bets" to="/pending-bets" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Won Bets" to="/won-bets" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Loss Bets" to="/loss-bets" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Refunded Bets" to="/refunded-bets" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="All Bets" to="/all-bets" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
        </Box>
      </Collapse> */}

      {/* <SectionHeader
        label="Declare Outcomes"
        icon={FaCoins}
        sectionKey="declare-outcomes"
        isOpen={openSection === "declare-outcomes"}
        toggleSection={toggleSection}
      />
      <Collapse in={openSection === "declare-outcomes"}>
        <Box ml={4} mt={1}>
          <NavItem label="Pending Outcomes" to="/pending-outcomes" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Declared Outcomes" to="/declared-outcomes" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
        </Box>
      </Collapse> */}

      <Text
        fontSize="xs"
        mt={6}
        mb={2}
        px={4}
        opacity={0.6}
        fontWeight="bold"
        letterSpacing="wider"
        textAlign="left"
      >
        MANAGE FINANCE
      </Text>
      <SectionHeader
        label="Deposits"
        icon={MdOutlineReceiptLong}
        sectionKey="deposits"
        isOpen={openSection === "deposits"}
        toggleSection={toggleSection}
      />
      <Collapse in={openSection === "deposits"}>
        <Box ml={4} mt={1}>
          <NavItem label="Pending Deposits" to="/deposit-pending" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Approved Deposits" to="/deposit-approved" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Successful Deposits" to="/deposit-successful" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Rejected Deposits" to="/deposit-rejected" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="All Deposits" to="/deposit-all" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
        </Box>
      </Collapse>

      <SectionHeader
        label="Withdrawals"
        icon={FaLandmark}
        sectionKey="withdrawals"
        isOpen={openSection === "withdrawals"}
        toggleSection={toggleSection}
      />
      <Collapse in={openSection === "withdrawals"}>
        <Box ml={4} mt={1}>
          <NavItem label="Pending Withdrawals" to="/withdrawal-pending" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Approved Withdrawals" to="/withdrawal-approved" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Rejected Withdrawals" to="/withdrawal-rejected" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="All Withdrawals" to="/withdrawal-all" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
        </Box>
      </Collapse>

      <NavItem icon={FaCoins} label="Bonus Settings" to="/bonus-settings" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />

      <Text
        fontSize="xs"
        mt={6}
        mb={2}
        px={4}
        opacity={0.6}
        fontWeight="bold"
        letterSpacing="wider"
        textAlign="left"
      >
        SETTINGS
      </Text>
      <NavItem
        label="Frontend Settings"
        to="/frontend-settings"
        icon={FiGlobe}
        onClick={onClose}
        sidebarRef={sidebarRef}
        scrollPos={scrollPos}
      />
      <NavItem
        label="Cron Jobs"
        to="/cron-jobs"
        icon={FiClock}
        onClick={onClose}
        sidebarRef={sidebarRef}
        scrollPos={scrollPos}
      />

      {/* <Text
        fontSize="xs"
        mt={6}
        mb={2}
        px={4}
        opacity={0.6}
        fontWeight="bold"
        letterSpacing="wider"
        textAlign="left"
      >
        OTHERS
      </Text>
      <SectionHeader
        label="Support Ticket"
        icon={IoTicketOutline}
        sectionKey="support"
        isOpen={openSection === "support"}
        toggleSection={toggleSection}
      />
      <Collapse in={openSection === "support"}>
        <Box ml={4} mt={1}>
          <NavItem label="Pending Ticket" to="/pending-ticket" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Closed Ticket" to="/closed-ticket" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Answered Ticket" to="/answered-ticket" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="All Ticket" to="/all-ticket" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
        </Box>
      </Collapse> */}

      {/* <SectionHeader
        label="Report"
        icon={MdOutlineAssessment}
        sectionKey="reports"
        isOpen={openSection === "reports"}
        toggleSection={toggleSection}
      />
      <Collapse in={openSection === "reports"}>
        <Box ml={4} mt={1}>
          <NavItem label="Transaction History" to="/transaction-history" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Login History" to="/login-history" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Notification History" to="/notification-history" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Referral Commissions" to="/referral-commissions" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Casino Reports" to="/reports/casino" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Sports Reports" to="/reports/sports" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
        </Box>
      </Collapse> */}

      {/* <SectionHeader
        label="Extra"
        icon={FiDatabase}
        sectionKey="extra"
        isOpen={openSection === "extra"}
        toggleSection={toggleSection}
      />
      <Collapse in={openSection === "extra"}>
        <Box ml={4} mt={1}>
          <NavItem label="Application" to="/extra-application" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Server" to="/extra-server" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Cache" to="/extra-cache" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
          <NavItem label="Update" to="/extra-update" onClick={onClose} sidebarRef={sidebarRef} scrollPos={scrollPos} />
        </Box>
      </Collapse> */}

      {/* <NavItem
        label="Report & Request"
        to="/report-request"
        icon={AiOutlineBug}
        onClick={onClose}
        sidebarRef={sidebarRef}
        scrollPos={scrollPos}
      /> */}
    </Box>
  );

  return (
    <>
      <IconButtonMain onOpen={drawer.onOpen} />

      {/* DESKTOP SIDEBAR */}
      <Box
        display={{ base: "none", md: "block" }}
        position="fixed"
        top="64px"
        left="0"
        zIndex={100}
      >
        <SidebarContent
          onClose={drawer.onClose}
          sidebarRef={sidebarRef}
          scrollPos={scrollPos}
          openSection={openSection}
          toggleSection={toggleSection}
        />
      </Box>

      {/* MOBILE DRAWER */}
      <Drawer isOpen={drawer.isOpen} placement="left" onClose={drawer.onClose}>
        <DrawerOverlay />
        <DrawerContent bg="#0b1b5c">
          <DrawerCloseButton color="white" />
          <DrawerBody p={0}>
            <SidebarContent
              onClose={drawer.onClose}
              sidebarRef={sidebarRef}
              scrollPos={scrollPos}
              openSection={openSection}
              toggleSection={toggleSection}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

const IconButtonMain = ({ onOpen }) => (
  <Box
    position="fixed"
    top="14px"
    left="14px"
    zIndex={2000}
    display={{ base: "block", md: "none" }}
    onClick={onOpen}
  >
    <Icon as={FiMenu} boxSize={6} color="white" cursor="pointer" />
  </Box>
);

export default Sidebar;
